import type { CustomerProfileView } from '../models/customer-profile.types';
import { parseCustomerProfileFromJson } from './customer-profile-normalize';

const STORAGE_KEY = 'redeeme_customer_auth';

export interface PersistedCustomerAuth {
  token: string | null;
  email?: string | null;
  /** Profile image URL when returned by verify-OTP or restored from storage. */
  avatarUrl?: string | null;
  /** Full profile snapshot from `GET /api/auth/customer/me` (and after login). */
  customerProfile?: CustomerProfileView | null;
}

/** Best-effort avatar URL from verify-OTP / login payloads (multiple API shapes). */
export function extractCustomerAvatarUrl(body: unknown): string | null {
  return pickAvatarDeep(body, new WeakSet<object>());
}

function pickAvatarDeep(body: unknown, seen: WeakSet<object>): string | null {
  if (body == null || typeof body !== 'object') {
    return null;
  }
  if (seen.has(body)) {
    return null;
  }
  seen.add(body);
  const o = body as Record<string, unknown>;
  const keys = ['img_url', 'avatarUrl', 'avatar_url', 'photoUrl', 'picture', 'imageUrl'];
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string') {
      const u = v.trim();
      if (u.length > 0) return u;
    }
  }
  const nestedKeys = ['data', 'customer', 'user', 'profile'];
  for (const nk of nestedKeys) {
    const inner = o[nk];
    const found = pickAvatarDeep(inner, seen);
    if (found) return found;
  }
  return null;
}

/** Best-effort token extraction from various API response shapes (no Bearer prefix). */
export function extractCustomerAccessToken(body: unknown): string | null {
  if (typeof body === 'string' && body.trim().length > 20) {
    return body.trim();
  }
  if (body == null || typeof body !== 'object') {
    return null;
  }
  const o = body as Record<string, unknown>;
  const direct =
    pickTokenString(o['token']) ??
    pickTokenString(o['access_token']) ??
    pickTokenString(o['accessToken']) ??
    pickTokenString(o['jwt']);
  if (direct) {
    return direct;
  }
  const data = o['data'];
  if (data != null && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    return (
      pickTokenString(d['token']) ??
      pickTokenString(d['access_token']) ??
      pickTokenString(d['accessToken']) ??
      null
    );
  }
  return null;
}

function pickTokenString(v: unknown): string | null {
  if (typeof v === 'string') {
    const t = v.trim();
    return t.length > 0 ? t : null;
  }
  return null;
}

export function loadPersistedCustomerAuth(): PersistedCustomerAuth | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== 'object') {
      return null;
    }
    const p = parsed as Record<string, unknown>;
    const token = typeof p['token'] === 'string' ? p['token'] : null;
    const email = typeof p['email'] === 'string' ? p['email'] : null;
    const avatarUrl = typeof p['avatarUrl'] === 'string' ? p['avatarUrl'] : null;
    let customerProfile: CustomerProfileView | null = null;
    const cpRaw = p['customerProfile'];
    if (cpRaw != null && typeof cpRaw === 'object') {
      customerProfile = parseCustomerProfileFromJson(cpRaw);
    }
    return { token: token ?? null, email, avatarUrl, customerProfile };
  } catch {
    return null;
  }
}

export function savePersistedCustomerAuth(state: PersistedCustomerAuth): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

export function clearPersistedCustomerAuth(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
